import { formatCurrency } from '@sportninja/common/utils/utils'
import { teamBudgetDepositCents } from '@sportninja/common/features/registration/teamBudgetPayment/utils/teamBudgetDeposit'

/**
 * Calculate discounted price and format as currency
 * @param discount - Discount amount or percentage
 * @param type - Discount type ('percentage' or 'amount')
 * @param cost - Original cost
 * @param currency - Currency name for formatting
 * @returns Formatted currency string with discount applied
 */
export const calculateDiscountAndFormatCurrency = (
  discount: number | null | undefined,
  type: 'percentage' | 'amount',
  cost: number,
  currency: string
): string => {
  if (discount === null || discount === undefined) {
    return formatCurrency(cost, currency)
  }
  if (type === 'percentage') {
    return formatCurrency(cost - (cost * discount) / 100, currency)
  }
  return formatCurrency(cost - discount, currency)
}

/**
 * Calculate discount amount
 * @param discount - Discount amount or percentage
 * @param type - Discount type ('percentage' or 'amount')
 * @param cost - Original cost
 * @returns Calculated discount amount
 */
export const calculateDiscount = (
  discount: number | null | undefined,
  type: 'percentage' | 'amount',
  cost: number
): number => {
  if (discount === null || discount === undefined) {
    return 0
  }
  if (type === 'percentage') {
    return (cost * discount) / 100
  }
  return discount
}

/** The division fields the card's deposit reads. */
export interface CardDepositDivision {
  deposit?: number | null
  player_deposit?: number | null
  cost_distribution_enabled?: boolean | null
  cost_distribution_threshold?: number | string | null
}

/**
 * Sep 24 (R6) — the deposit the card states, and whether a coupon may strike its
 * figures through.
 *
 * On a Team Budget division a team rep's deposit is the division's Minimum To
 * Qualify (never its legacy `deposit` column), and a coupon discounts nothing: the
 * session prices at zero and the fee is split among the players. Players and every
 * other division read exactly as before.
 */
export const resolveCardDeposit = ({
  division,
  isTeamRep,
  hasCoupon,
}: {
  division: CardDepositDivision
  isTeamRep: boolean
  hasCoupon: boolean
}): { deposit: number | null | undefined; couponApplies: boolean } => {
  if (isTeamRep && division.cost_distribution_enabled) {
    const cents = teamBudgetDepositCents(division)

    return {
      deposit: cents === null ? null : cents / 100,
      couponApplies: false,
    }
  }

  return {
    deposit: isTeamRep ? division.deposit : division.player_deposit,
    couponApplies: hasCoupon,
  }
}
